import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Carousel.scss';

/**
 * 大屏轮播组件
 * @param {Array} slides 幻灯片数组，每项 { title, subtitle?, desc?, bg, theme?, cta?:{text,to} | {text, scrollTo} }
 *   - cta.to: 路由跳转地址
 *   - cta.scrollTo: 点击平滑滚动到对应元素 id（手动轮播场景使用）
 * @param {number} interval 自动轮播间隔(ms)，传 0 或负数则关闭自动轮播，仅支持手动切换
 */
function Carousel({ slides, interval = 0 }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const go = (i) => setIndex(((i % slides.length) + slides.length) % slides.length);
  const next = () => go(index + 1);
  const prev = () => go(index - 1);

  // 关闭自动轮播（interval <= 0）或仅 1 张时直接返回
  useEffect(() => {
    if (paused || slides.length <= 1 || interval <= 0) return undefined;
    timer.current = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, interval);
    return () => clearInterval(timer.current);
  }, [paused, interval, slides.length]);

  // CTA 点击：支持平滑滚动到页面指定区块
  const handleCta = (cta) => {
    if (cta.scrollTo) {
      const el = document.getElementById(cta.scrollTo);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    // 路由跳转交由 <Link> 处理
  };

  return (
    <section
      className="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
    >
      <div
        className="carousel-track"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {slides.map((s, i) => (
          <div
            className="carousel-slide"
            key={i}
            style={{ background: s.bg }}
            aria-hidden={i !== index}
          >
            <div
              className={`container carousel-content ${
                s.theme === 'dark' ? 'is-dark' : ''
              }`}
            >
              <h1>{s.title}</h1>
              {s.subtitle && <h2>{s.subtitle}</h2>}
              {s.desc && <p>{s.desc}</p>}
              {s.cta && (
                s.cta.scrollTo ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleCta(s.cta)}
                  >
                    {s.cta.text}
                  </button>
                ) : (
                  <Link to={s.cta.to} className="btn btn-primary">
                    {s.cta.text}
                  </Link>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button
            className="carousel-arrow prev"
            onClick={prev}
            aria-label="上一张"
          >
            ‹
          </button>
          <button
            className="carousel-arrow next"
            onClick={next}
            aria-label="下一张"
          >
            ›
          </button>
          <div className="carousel-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={i === index ? 'dot active' : 'dot'}
                onClick={() => go(i)}
                aria-label={`第 ${i + 1} 张`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export default Carousel;
